import { isAxiosError } from "axios";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/features/auth";
import { authStorageService } from "@/features/auth/services/storage.service";
import { clearCmsPreviewToken, CMS_PREVIEW_PAGE_CODE } from "@/features/shop/cms/preview/cmsPreviewToken";
import { useCmsPreviewToken } from "@/features/shop/cms/preview/useCmsPreviewToken";
import { getCmsPageByCode } from "@/features/shop/cms/services/cms.service";
import type { CmsPageDetail } from "@/features/shop/cms/types/cms.types";

type UseCmsPageByCodeProps = {
  code: string
  enabled?: boolean
};

const PREVIEW_END_ERRORS = ["preview_expired", "preview_gone"];
const PREVIEW_END_FALLBACK_MESSAGE = "L'aperçu n'est plus disponible : retour à la version en ligne.";

/**
 * Clé distincte pour l'aperçu : le cache de la page en ligne n'est jamais pollué par un brouillon.
 */
export const cmsPageQueryKey = (code: string, previewToken: string | null = null) =>
  (previewToken ? ["cmsPage", code, "preview", previewToken] : ["cmsPage", code]);

const previewEndMessage = (error: unknown): string | null => {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as { error?: unknown, message?: unknown } | undefined;
  if (typeof data?.error !== "string" || !PREVIEW_END_ERRORS.includes(data.error)) return null;
  return typeof data.message === "string" && data.message ? data.message : PREVIEW_END_FALLBACK_MESSAGE;
};

const fetchPreview = async (code: string, accessToken: string, previewToken: string) => {
  try {
    return await getCmsPageByCode(code, accessToken, previewToken);
  }
  catch (error) {
    const message = previewEndMessage(error);
    if (message) {
      // Jeton expiré (403) ou brouillon publié/abandonné (410) : on revient à la version en ligne.
      // Dans le queryFn, et non dans un effet, pour un seul toast quel que soit le nombre de
      // composants abonnés à la page.
      clearCmsPreviewToken();
      toast.error(message);
    }
    throw error;
  }
};

export const useCmsPageByCode = ({
  code,
  enabled = true,
}: UseCmsPageByCodeProps) => {
  const { isAuthenticated } = useAuth();
  const accessToken = authStorageService.getAuthToken();
  const storedPreviewToken = useCmsPreviewToken();
  const previewToken = code === CMS_PREVIEW_PAGE_CODE ? storedPreviewToken : null;

  return useQuery<CmsPageDetail>({
    queryKey: cmsPageQueryKey(code, previewToken),
    queryFn: () => (previewToken
      ? fetchPreview(code, accessToken ?? "", previewToken)
      : getCmsPageByCode(code, accessToken ?? "")),
    enabled: enabled && isAuthenticated && !!accessToken && !!code,
    // Un brouillon bouge pendant qu'on le regarde : on le relit à chaque retour sur la boutique.
    staleTime: previewToken ? 30 * 1000 : 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Pas de nouvel essai sur un aperçu terminé : le jeton est déjà effacé.
    ...(previewToken && { retry: (failureCount: number, error: Error) => !previewEndMessage(error) && failureCount < 1 }),
    meta: { errorConfig: { silent: true } },
  });
};
